import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import AnalyticsOutlined from '@mui/icons-material/AnalyticsOutlined';
import PageInfoIcon from '@/components/icons/configuration';
import { paths } from '@/constants/paths';
import { NavigationItem } from '.';

/**
 * TEMP(FUE-236): Static firm admin nav tree.
 *
 * Firm users currently bypass UserInfo.Menu and use this list instead (see sidebar.tsx).
 * Remove this override when a firm/admin nav-settings module controls visible menu items
 * via the backend (same pipeline as other roles).
 *
 * Module visibility (e.g. eConsents) is NOT filtered here yet — all items show until
 * that settings module exists.
 */
export type FirmNavItem = NavigationItem & {
  /** Resolved via useLocalization term key instead of firmNav i18n key. */
  termKey?: string;
  /** Label uses firmNav.advisorList with the Advisor term interpolated. */
  useAdvisorListLabel?: boolean;
  /**
   * Base path used to decide whether this item (and its sidebar dropdown) is
   * "active", when that differs from the navigable `route` — e.g. a section
   * whose nav item links straight to its default sub-page but whose other
   * sub-pages (reached via in-page tabs, not the sidebar) should still count
   * as active. Falls back to `route` when omitted.
   */
  activeMatch?: string;
  children?: FirmNavItem[];
};

const firmNavigationItems: FirmNavItem[] = [
  {
    text: 'firmNav.dashboard',
    route: paths.firm.dashboard,
    icon: <DashboardOutlinedIcon />,
  },
  {
    text: 'Advisors',
    termKey: 'Advisors',
    icon: <GroupsOutlined />,
    children: [
      {
        text: 'firmNav.advisorList',
        useAdvisorListLabel: true,
        route: paths.firm.advisorServices.advisors,
      },
      {
        text: 'firmNav.activity',
        route: paths.firm.advisorServices.activity,
      },
    ],
  },
  {
    text: 'firmNav.clientUsage',
    icon: <PersonOutlineRounded />,
    children: [
      {
        text: 'firmNav.activity',
        route: paths.firm.clientUsage.activity,
      },
      {
        text: 'firmNav.eConsents',
        route: paths.firm.clientUsage.eConsents,
      },
    ],
  },
  {
    text: 'firmNav.analytics',
    icon: <AnalyticsOutlined />,
    children: [
      {
        text: 'Asset Allocation',
        termKey: 'Asset Allocation',
        route: paths.firm.analytics.assetAllocation,
      },
    ],
  },
  {
    text: 'firmNav.configuration',
    icon: <PageInfoIcon />,
    children: [
      {
        text: 'firmNav.groupsAndUsers',
        route: paths.firm.configuration.groupsAndUsers,
      },
      {
        text: 'firmNav.portalSettings',
        route: paths.firm.configuration.portalSettings,
      },
      {
        text: 'firmNav.clientRelationships',
        route: paths.firm.configuration.clientRelationships,
      },
      {
        text: 'firmNav.dataFeeds',
        route: paths.firm.configuration.dataFeeds.managedAccounts,
        activeMatch: paths.firm.configuration.dataFeeds.index,
      },
      {
        text: 'firmNav.integrations',
        route: paths.firm.configuration.integrations,
      },
    ],
  },
];

export default firmNavigationItems;

/** Collects route paths from the firm nav tree (tests, future permission wiring). */
export function collectFirmNavRoutes(
  items: FirmNavItem[] = firmNavigationItems,
): string[] {
  const routes: string[] = [];
  const visit = (nodes: FirmNavItem[]) => {
    for (const node of nodes) {
      if (node.route) {
        routes.push(node.route);
      }
      if (node.children) {
        visit(node.children);
      }
    }
  };
  visit(items);
  return routes;
}
